// Starting values for a new plan. Kept free of React and platform imports so
// the calculation tests can use them.
// Adjustable rules of the flexible withdrawal methods, in the units the user types.
export const DEFAULT_STRATEGY_SETTINGS = {
  bucketYears: 2,
  guardrailBand: 20,
  guardrailCut: 10,
  guardrailRaise: 10,
  vanguardCeiling: 5,
  vanguardFloor: 2.5,
};

// The sample household a first visit opens on: an ordinary couple in their
// thirties, close enough to the line that the levers visibly move the answer.
export const DEFAULT_PROFILE = {
  filingStatus: 'married_filing_jointly',
  spouse1: { name: 'You', currentAge: 35, retirementAge: 60 },
  // `income` is the spouse's own pay; it stops at the spouse's retirement age.
  spouse2: { name: 'Spouse', currentAge: 34, retirementAge: 60, income: 0 },
  lifeExpectancy: 90,
  dependents: 0,
  income: 110000,
  annualSpending: 72000,
  // The plan as it stood on a day the person chose, to measure against later (utils/planBaseline.js).
  baseline: null,
  // Property held as an investment: { label, value, netIncome, growth, sellAge, owedAtSale, sellCostPct }
  properties: [],
  // The lines of the spending worksheet, if it was used: { rows: { id: monthly }, skipHealth, skipMortgage }.
  budget: null,
  annualSavings: 16500,
  savingsGrowthRate: 3.0,
  // How savings are set: a dollar 'amount' (above), or a 'percent' of income.
  savingsMode: 'percent',
  savingsRate: 15,
  // Where new savings go: { traditional, roth, taxable } in percent. Null: the
  // mix of the accounts held today (all traditional when there are none).
  savingsSplit: null,
  // Raises above inflation, until the age pay levels off.
  incomeGrowthRate: 1.5,
  incomeGrowthUntilAge: 55,
  // Age at which pay starts, for someone not yet working. Null: working now.
  workStartAge: null,
  accounts: { trad401k: 60000, roth401k: 0, tradIRA: 10000, rothIRA: 25000, hsa: 5000, taxable: 20000 },
  matchPctOfSalary: 6,
  matchRate: 50,
  hsaEligible: true,
  ss1: 2900,
  ss2: 1300,
  // Claiming ages default to full retirement age when unset.
  ss1ClaimAge: 67,
  ss2ClaimAge: 67,
  // lumpSum: a one-off payment offered in place of the pension. survivorPct:
  // the share that carries on for the other spouse.
  pension1: { startAge: 65, annualAmount: 0, cola: false, lumpSum: 0, survivorPct: 50 },
  pension2: { startAge: 65, annualAmount: 0, cola: false, lumpSum: 0, survivorPct: 50 },
  // What was paid for the taxable account. Null means "not known".
  taxableCostBasis: null,
  // What was paid into Roth accounts (reachable at any age). Null: not known.
  rothContributions: null,
  // Leaving the employer at 55 or later frees that 401(k) from the early penalty.
  ruleOf55: false,
  // Two-letter state code: that state's own income tax rules. '' for none chosen.
  state: '',
  // Flat state income tax rate on retirement income, in percent, used when no
  // state is chosen. 0: none.
  stateTaxRate: 0,
  localTaxRate: 0,
  // Health coverage between retiring and Medicare at 65: 'entered' (the yearly
  // cost below) or 'estimate' (worked out from the household each year).
  healthCoverMode: 'entered',
  // For the estimate: care each person pays for themselves in a year, and the
  // ages today of children on the plan, as typed ("12, 15").
  healthOutOfPocket: 0,
  childrenAges: '',
  // Yearly cost of health coverage between retiring and Medicare at 65.
  healthCostBeforeMedicare: 15000,
  // Full yearly price of the benchmark marketplace plan. 0: no premium credit worked out.
  acaBenchmarkPremium: 0,
  // Add the standard Medicare Part B premium from 65.
  medicarePremiums: false,
  // Care late in life: a yearly cost for a number of years from an age.
  longTermCare: { startAge: 85, years: 0, annualCost: 0 },
  // Yearly cost of investing, in percent, and a view on returns.
  investmentFee: 0,
  returnOutlook: 'average',
  // A stock/bond mix that moves to another one between two ages. to: '' for none.
  glide: { to: '', fromAge: 55, toAge: 65 },
  // Income in retirement besides pensions and Social Security:
  // { label, kind: 'work' | 'taxable' | 'taxFree', annual, fromAge, toAge }
  otherIncome: [],
  // One-off money in (positive) or out (negative) at an age: { label, age, amount }
  lumpSums: [],
  // Spending as a percent of its starting level from an age: { fromAge, percent }
  spendingSteps: [],
  // A mortgage payment that ends at an age.
  // included: the spending figure already covers the payment, so nothing is
  // added and spending falls at the payoff age.
  mortgage: { annualPayment: 0, payoffAge: 0, included: false },
  // The plan must end with at least this much. 0: no target.
  legacyTarget: 0,
  // What one spouse alone would spend, as a percent of the household's spending,
  // and the age at which the "one of you dies" comparison is made (0: ten years in).
  survivorSpendingPct: 75,
  survivorAge: 0,
  // A what-if: a lifetime income bought with part of the portfolio.
  annuity: { amount: 0, age: 65, payoutRate: 0 },
  // A what-if: a home whose equity could be released if savings ran out.
  // releasePct: the share of the equity that could be turned into money.
  home: { value: 0, owed: 0, releasePct: 50 },
  withdrawalOrder: 'proportional',
  rothConversionRate: null,
  // 'steady': conversions every year as scheduled. 'onPlan': only in a year the
  // portfolio is not far behind the plan's projection. 'catchUp': the same, and
  // skipped conversions are made up in later years that are on plan.
  conversionPace: 'steady',
  // Stop a conversion short of the next Medicare surcharge tier in a year whose income sets one.
  conversionIrmaa: false,
  seppMode: 'none',
  seppAmount: 0,
  strategySettings: { ...DEFAULT_STRATEGY_SETTINGS },
};

// "Start blank": keeps ages and filing status, zeroes every dollar amount.
export const BLANK_PROFILE = {
  income: 0,
  annualSpending: 0,
  annualSavings: 0,
  accounts: { trad401k: 0, roth401k: 0, tradIRA: 0, rothIRA: 0, hsa: 0, taxable: 0 },
  ss1: 0,
  ss2: 0,
  pension1: { annualAmount: 0 },
  pension2: { annualAmount: 0 },
  healthCostBeforeMedicare: 0,
  taxableCostBasis: null,
  rothContributions: null,
};

// "Just starting out": a first plan for someone at or before the start of a
// career. Every figure is a placeholder to replace; none is a suggestion.
export const STARTER_PROFILE = {
  filingStatus: 'single',
  spouse1: { name: 'You', currentAge: 17, retirementAge: 60 },
  lifeExpectancy: 95,
  income: 50000,
  workStartAge: 22,
  incomeGrowthRate: 3,
  incomeGrowthUntilAge: 50,
  savingsMode: 'percent',
  savingsRate: 15,
  annualSavings: 0,
  annualSpending: 50000,
  accounts: { trad401k: 0, roth401k: 0, tradIRA: 0, rothIRA: 0, hsa: 0, taxable: 0 },
  taxableCostBasis: null,
  rothContributions: null,
  matchPctOfSalary: 4,
  matchRate: 100,
  hsaEligible: false,
  // New savings split between traditional and Roth: early in a career, pay is
  // taxed lightly, and an all-traditional default would put an early retiree's
  // money behind the 10% penalty.
  savingsSplit: { traditional: 50, roth: 50, taxable: 0 },
  // Health coverage before Medicare worked out from the household, not left at zero.
  healthCoverMode: 'estimate',
  ss1: 2000,
  ss1ClaimAge: 67,
  pension1: { annualAmount: 0 },
};

export const DEFAULT_CONFIG = {
  initialInvestment: 120000,
  years: 25,
  annualContribution: 16500,
  savingsGrowthRate: 3.0,
  riskProfile: 'balanced',
  numberOfSimulations: 10000,
  inflationRate: 2.5,
};

export const DEFAULT_DRAWDOWN_PHASES = [
  { startAge: 65, endAge: 75, annualAmount: 80000, description: 'Early retirement phase' },
  { startAge: 75, endAge: 85, annualAmount: 65000, description: 'Reduced expenses' },
  { startAge: 85, endAge: 90, annualAmount: 50000, description: 'Late retirement + Social Security' },
];
